import { OAuthPopupFlow as PackageOAuthPopupFlow, isOAuthFlowError } from '@ringba/oauth-popup';

/**
 * Adapter around the shared @ringba/oauth-popup flow, preserving v2's public
 * surface: constructor(oauthService, tpaBaseUrl), start(), authorize(), cancel(), dispose().
 */
export default class OAuthPopupFlow {
  constructor(oauthService, tpaBaseUrl) {
    this._oauthService = oauthService;
    this._flow = new PackageOAuthPopupFlow(tpaBaseUrl);
    this._disposed = false;
  }

  /**
   * Run the full OAuth popup flow and save the code as a NEW integration.
   * Must be called directly from the user click handler (see authorize()).
   *
   * @param {string} accountId
   * @param {string} provider
   * @returns {Promise<Object>} Integration data from the API
   */
  async start(accountId, provider) {
    const { code, redirectUrl } = await this.authorize(accountId, provider);

    try {
      const saveResponse = await this._oauthService.saveOauthData(accountId, provider, {
        code,
        redirectUrl,
      });
      return saveResponse.data;
    } catch (error) {
      throw new Error('Failed to save OAuth data: ' + (error.message || error), { cause: error });
    }
  }

  /**
   * Run the OAuth popup flow up to the authorization code, without saving anything.
   * Must be called directly from the user click handler: the popup opens synchronously,
   * before the first await, to avoid popup blockers.
   *
   * @param {string} accountId
   * @param {string} provider
   * @returns {Promise<{code: string, redirectUrl: string}>} The code and the redirect URL it was issued for
   */
  async authorize(accountId, provider) {
    if (this._disposed) {
      throw new Error('OAuthPopupFlow has been disposed');
    }

    // Must be synchronous, before any await, to avoid popup blockers.
    try {
      this._flow.openPopup();
    } catch (error) {
      throw translateFlowError(error);
    }

    const redirectUrl = this._flow.getRedirectUrl();
    let authUrl;
    try {
      const response = await this._oauthService.getAuthUrl(accountId, provider, redirectUrl);
      authUrl = response.data.authUrl;
    } catch (error) {
      this.dispose();
      throw new Error('Failed to get auth URL: ' + (error.message || error), { cause: error });
    }

    try {
      const code = await this._flow.continueWithAuthUrl(authUrl);
      return { code, redirectUrl };
    } catch (error) {
      throw translateFlowError(error);
    } finally {
      this.dispose();
    }
  }

  cancel() {
    this._flow.cancel();
  }

  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this._flow.dispose();
  }
}

// Maps package OAuthError types to the message strings utilities.errorHandler() expects.
// Codes with no dedicated mapping fall through to the generic message.
function translateFlowError(error) {
  if (!isOAuthFlowError(error)) {
    return error instanceof Error ? error : new Error(String(error));
  }

  const { type, message } = error.oauthError;
  switch (type) {
  case 'popupBlocked':
    return new Error('Popup was blocked by the browser');
  case 'closedEarly':
    return new Error('OAuth popup was closed');
  case 'timeout':
    return new Error('OAuth handshake timed out');
  case 'stateMismatch':
    return new Error('OAuth state mismatch — possible CSRF');
  case 'providerError':
    return new Error('OAuth error: ' + (message || 'unknown'));
  case 'handshakeFailed':
  default:
    return new Error(message || 'OAuth flow failed');
  }
}
